import { useCallback, useEffect, useState } from 'react';
import { AppWindow, MonitorUp, RefreshCw } from 'lucide-react';
import { Button, Modal, Skeleton, cn } from '@cyphex/ui';
import { useStoreValue } from '@/lib/tinyStore';
import { desktopBridge } from '../bridge';
import {
  screenPickerStore,
  selectPickerOpen,
  settleScreenSource,
} from '../screenSources';
import type { ScreenSource } from '../types';

type Tab = 'screen' | 'window';

const THUMBNAIL_WIDTH = 320;

/**
 * The real native source picker — every screen and every open window, with live
 * thumbnails, which is exactly what a browser refuses to show a page.
 * Rendered globally (see DesktopRuntime) because a share can be started from
 * a meeting, a voice room or the call bar.
 */
export function ScreenSourcePicker() {
  const open = useStoreValue(screenPickerStore, selectPickerOpen);
  const [sources, setSources] = useState<readonly ScreenSource[]>([]);
  const [tab, setTab] = useState<Tab>('screen');
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    const bridge = desktopBridge();
    if (bridge === null) return;
    setLoading(true);
    bridge
      .getScreenSources(THUMBNAIL_WIDTH)
      .then((next) => {
        setSources(next);
        setSelected((current) =>
          current !== null && next.some((item) => item.id === current) ? current : null,
        );
      })
      .catch(() => setSources([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab('screen');
    setSelected(null);
    load();
  }, [open, load]);

  const cancel = useCallback(() => settleScreenSource(null), []);
  const share = useCallback(() => {
    if (selected !== null) settleScreenSource(selected);
  }, [selected]);

  const visible = sources.filter((source) => source.kind === tab);
  const screenCount = sources.filter((source) => source.kind === 'screen').length;
  const windowCount = sources.length - screenCount;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) cancel();
      }}
      title="Choose what to share"
      description="Pick a screen or a single window. Only what you pick is sent."
      size="xl"
      footer={
        <>
          <Button variant="ghost" leftIcon={RefreshCw} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outline-neutral" onClick={cancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={share} disabled={selected === null}>
            Share
          </Button>
        </>
      }
    >
      <div className="mb-3 flex gap-2" role="tablist" aria-label="Source type">
        <Button
          role="tab"
          aria-selected={tab === 'screen'}
          variant={tab === 'screen' ? 'subtle' : 'ghost'}
          size="sm"
          leftIcon={MonitorUp}
          onClick={() => setTab('screen')}
        >
          Screens · {screenCount}
        </Button>
        <Button
          role="tab"
          aria-selected={tab === 'window'}
          variant={tab === 'window' ? 'subtle' : 'ghost'}
          size="sm"
          leftIcon={AppWindow}
          onClick={() => setTab('window')}
        >
          Windows · {windowCount}
        </Button>
      </div>

      <div className="max-h-[46vh] overflow-y-auto pr-1">
        {loading && sources.length === 0 ? (
          <div className="grid grid-cols-3 gap-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : visible.length === 0 ? (
          <p className="py-10 text-center text-md text-muted-foreground">
            {tab === 'screen'
              ? 'No screens were reported by the system.'
              : 'No shareable windows are open right now.'}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {visible.map((source) => (
              <SourceTile
                key={source.id}
                source={source}
                selected={selected === source.id}
                onSelect={setSelected}
                onConfirm={settleScreenSource}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

interface SourceTileProps {
  source: ScreenSource;
  selected: boolean;
  onSelect: (id: string) => void;
  onConfirm: (id: string) => void;
}

function SourceTile({ source, selected, onSelect, onConfirm }: SourceTileProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(source.id)}
      onDoubleClick={() => onConfirm(source.id)}
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-2 text-left transition-colors duration-fast',
        selected
          ? 'border-primary bg-primary-subtle'
          : 'border-border hover:bg-subtle focus-visible:bg-subtle',
      )}
    >
      <div className="flex h-24 items-center justify-center overflow-hidden rounded-md bg-secondary">
        {source.thumbnail.length > 0 ? (
          <img
            src={source.thumbnail}
            alt={`Preview of ${source.name}`}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <MonitorUp size={24} className="text-muted-foreground" aria-hidden />
        )}
      </div>
      <span className="flex items-center gap-1.5 truncate text-sm font-medium text-secondary-foreground">
        {source.appIcon.length > 0 ? (
          <img src={source.appIcon} alt="" aria-hidden className="h-4 w-4 shrink-0" />
        ) : null}
        <span className="truncate">{source.name}</span>
      </span>
    </button>
  );
}
